import { useEffect, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ErrorBlock } from '../components/ErrorBlock';
import { Layout } from '../components/Layout';
import { Loading } from '../components/Loading';
import { BRAND } from '../config';
import { LoginPage } from '../features/auth/LoginPage';
import { CasePage } from '../features/cases/CasePage';
import { CasesPage } from '../features/cases/CasesPage';
import { FeedbackPage } from '../features/feedback/FeedbackPage';
import { NotificationsPage } from '../features/notifications/NotificationsPage';
import { PrivacyPage } from '../features/privacy/PrivacyPage';
import { useI18n, useT } from '../i18n';
import { useSession } from './session';
import { useSite } from './site';

function RequireAuth({ children }: { children: ReactNode }) {
  const status = useSession((s) => s.status);
  const load = useSession((s) => s.load);
  const location = useLocation();
  if (status === 'unknown' || status === 'loading') return <Loading />;
  if (status === 'error') return <ErrorBlock error={null} onRetry={() => void load()} />;
  if (status === 'anon') return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

function NotFound() {
  const t = useT();
  return <p className="py-10 text-[16px] text-txt">{t('err.not_found')}</p>;
}

function DocumentMeta() {
  const locale = useI18n((s) => s.locale);
  const t = useT();
  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = `${t('common.cabinet')} · ${BRAND}`;
  }, [locale, t]);
  return null;
}

export function App() {
  const loadSession = useSession((s) => s.load);
  const loadSite = useSite((s) => s.load);
  const loadI18n = useI18n((s) => s.load);
  useEffect(() => {
    void loadSite();
    void loadI18n();
    void loadSession();
  }, [loadSession, loadSite, loadI18n]);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <DocumentMeta />
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to="/cases" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/cases"
            element={
              <RequireAuth>
                <CasesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/cases/:id"
            element={
              <RequireAuth>
                <CasePage />
              </RequireAuth>
            }
          />
          <Route
            path="/notifications"
            element={
              <RequireAuth>
                <NotificationsPage />
              </RequireAuth>
            }
          />
          {/* посилання з листа-запиту на відгук: без входу */}
          <Route path="/feedback/:token" element={<FeedbackPage />} />
          <Route
            path="/privacy"
            element={
              <RequireAuth>
                <PrivacyPage />
              </RequireAuth>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}
